import React, {useEffect, useState, useRef, useCallback} from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import classnames from 'classnames';
import moment from "moment";
import am4themes_animated from '@amcharts/amcharts4/themes/animated';

const LineColumnChart = ({
                           title, data, startDate, endDate, granularity, color, lineOn, columnOn, clickableRight, activeDate, hoveredDate,
                           setHoveredDate, handleClickDate, handleClickLeft, handleClickRight,
                         }) => {

  const [chart, setChart] = useState(null);
  const [dateAxis, setDateAxis] = useState(null);
  const getLabelByGranularity = (granularity) => {
    if (granularity === 'day') {
      return '일간';
    } else if (granularity === 'week') {
      return '주간';
    } else if (granularity === 'month') {
      return '월간';
    }
  };
  const currency = title === '애드센스' ? 'USD' : '원';
  const tooltipHtml = `
    <div style="min-width: 112px; height: 82px;">
      <div class="layer_popup graph_layer" style="position: fixed;margin-right: 10px">
        <div class="inner_popup inner_popup_type1">
          <strong class="tit_popup"><em>${title}</em> <span class="inner_g">${getLabelByGranularity(granularity)}</span></strong>
            <div class="current_num">
              <div class="num_val">
                <span class="num_graph">{lineValue}</span><span class="txt_graph">${currency}</span>
              </div>
              <div class="num_chg">
                <span class="num_g">{columnValue}</span>
                <span class="ico_blog ico_graph"></span>
              </div>
            </div>
        </div>
      </div>
    </div>
    `;
  useEffect(() => {
    const parent = document.getElementById("chartdiv");
    while (parent.hasChildNodes()) {
      parent.removeChild(parent.firstChild);
    }

    // 차트 생성
    am4core.addLicense("CH90809262");
    let chart = am4core.create("chartdiv", am4charts.XYChart);
    chart.data = data;
    chart.yAxesAndPlotContainer.height = 220;
    chart.chartAndLegendContainer.align = "center";
    chart.zoomOutButton.disabled = true;
    chart.cursor = new am4charts.XYCursor();
    chart.cursor.lineX.disabled = true;
    chart.cursor.lineY.disabled = true;
    chart.cursor.behavior = "none";
    chart.cursor.events.on('cursorpositionchanged', (ev) => {
      const xAxis = ev.target.chart.xAxes.getIndex(0);
      const date = xAxis.positionToDate(xAxis.toAxisPosition(ev.target.xPosition));
      const hovered = moment(date).startOf(granularity === 'week' ? 'isoweek' : granularity).toDate();
      setHoveredDate(hovered);
    });

    // x축 세팅
    let dateAxis = chart.xAxes.push(new am4charts.DateAxis());
    dateAxis.hidden = true;
    if (granularity === 'month') {
      dateAxis.dateFormatter.inputDateFormat = 'yyyy-MM';
      dateAxis.dateFormats.setKey(granularity, "M월");
      dateAxis.periodChangeDateFormats.setKey(granularity, "M월\nyyyy년");
      dateAxis.gridIntervals.setAll([
        {timeUnit: granularity, count: 1},
      ]);
    } else {
      if (granularity === "week") {
        dateAxis.renderer.labels.template.location = 0.5;
      }
      dateAxis.dateFormatter.inputDateFormat = 'yyyy-MM-dd';
      dateAxis.dateFormats.setKey(granularity, "d\nM월");
      dateAxis.periodChangeDateFormats.setKey(granularity, "d\nM월");
      dateAxis.gridIntervals.setAll([
        {timeUnit: granularity, count: 1},
      ]);
    }
    dateAxis.width = 740;
    dateAxis.align = "center";
    dateAxis.renderer.grid.template.disabled = true;
    dateAxis.tooltip.disabled = true;
    dateAxis.renderer.labels.template.events.on("hit", (ev) => {
      handleClickDate(ev.target.dataItem.date);
    });

    // line을 위한 y축 세팅
    let lineAxis = chart.yAxes.push(new am4charts.ValueAxis());
    lineAxis.min = 0;
    lineAxis.renderer.labels.template.disabled = true;
    lineAxis.renderer.grid.template.strokeDasharray = "3,2";
    lineAxis.renderer.grid.template.stroke = am4core.color("#b0bbc5");
    lineAxis.renderer.baseGrid.disabled = true;
    lineAxis.cursorTooltipEnabled = false;
    lineAxis.renderer.grid.template.adapter.add('stroke', (val, target) => {
      if (target.dataItem.itemIndex === 2) {
        return am4core.color('white');
      } else {
        return val;
      }
    });

    // column을 위한 y축 세팅
    let columnAxis = chart.yAxes.push(new am4charts.ValueAxis());
    columnAxis.min = 0;
    columnAxis.renderer.labels.template.disabled = true;
    columnAxis.syncWithAxis = lineAxis;
    columnAxis.renderer.grid.template.strokeDasharray = "3,2";
    columnAxis.renderer.grid.template.stroke = am4core.color("#b0bbc5");
    columnAxis.renderer.baseGrid.disabled = true;
    columnAxis.cursorTooltipEnabled = false;
    columnAxis.renderer.grid.template.adapter.add('stroke', (val, target) => {
      if (target.dataItem.itemIndex === 2) {
        return am4core.color('white');
      } else {
        return val;
      }
    });

    // column 생성
    let columnSeries = chart.series.push(new am4charts.ColumnSeries());
    columnSeries.dataFields.dateX = 'date';
    columnSeries.dataFields.valueY = 'columnValue';
    columnSeries.columns.template.strokeWidth = 0;
    columnSeries.columns.template.column.cornerRadius(1.5, 1.5, 1.5, 1.5);
    columnSeries.columns.template.contentValign = 'bottom';
    columnSeries.columns.template.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    columnSeries.columns.template.tooltipY = 0;
    columnSeries.columns.template.width = 4;
    columnSeries.tooltip.background.disabled = true;
    columnSeries.tooltip.pointerOrientation = "left";
    columnSeries.tooltip.dx = -36;
    columnSeries.tooltip.dy = -47;
    columnSeries.xAxis = dateAxis;
    columnSeries.yAxis = columnAxis;
    columnSeries.fill = am4core.color("#b0bbc5");
    const columnHoverState = columnSeries.columns.template.states.create('hover');
    columnHoverState.properties.width = 6;
    columnHoverState.properties.dx = -1;
    if (!lineOn) {
      columnSeries.columns.template.tooltipHTML = tooltipHtml;
    }
    columnSeries.columns.template.adapter.add('width', (wid, target) => {
      if (target.dataItem && target.dataItem.dateX.getTime() === activeDate.getTime()) {
        return 6.01;
      } else {
        return wid;
      }
    });
    columnSeries.columns.template.adapter.add('dx', (dx, target) => {
      if (target.dataItem && target.dataItem.dateX.getTime() === activeDate.getTime()) {
        return -1;
      } else {
        return dx;
      }
    });
    columnSeries.columns.template.column.events.on("focus", (ev) => {
      handleClickDate(ev.target.dataItem.dateX);
    });
    if (!columnOn) {
      columnSeries.hide();
    }

    // line 생성
    let lineSeries = chart.series.push(new am4charts.LineSeries());
    lineSeries.dataFields.dateX = 'date';
    lineSeries.dataFields.valueY = 'lineValue';
    lineSeries.tooltip.background.disabled = true;
    lineSeries.tooltip.pointerOrientation = "left";
    lineSeries.tooltip.dx = -36;
    lineSeries.tooltip.dy = -47;
    lineSeries.xAxis = dateAxis;
    lineSeries.yAxis = lineAxis;
    lineSeries.stroke = am4core.color(color);
    lineSeries.strokeWidth = 3;
    lineSeries.bulletsContainer.parent = chart.seriesContainer;

    // bullet(동그란 circle 생성)
    let bullet = lineSeries.bullets.push(new am4charts.CircleBullet());
    bullet.configField = 'bulletConfig';
    bullet.circle.strokeWidth = 2;
    bullet.circle.radius = 4.5;
    bullet.circle.cursorOverStyle = am4core.MouseCursorStyle.pointer;
    bullet.fill = am4core.color('white');
    bullet.tooltipHTML = tooltipHtml;
    const bulletHoverState = bullet.states.create('hover');
    bulletHoverState.properties.fill = am4core.color(color);
    bullet.circle.adapter.add('fill', (co, target, key) => {
      if (target.dataItem && target.dataItem.dateX.getTime() === activeDate.getTime()) {
        return am4core.color(color);
      } else {
        return co;
      }
    });
    bullet.events.on('focus', (ev) => {
      handleClickDate(ev.target.dataItem.dateX);
    });

    if (!lineOn) {
      lineSeries.hide();
    }
    setChart(chart);
    setDateAxis(dateAxis);

    return () => {
      chart.dispose();
    }
  }, [
    data, color, lineOn, columnOn, activeDate
  ]);

  useEffect(() => {
    if (data && chart && dateAxis) {
      if (hoveredDate) {
        const point = dateAxis.dateToPoint(hoveredDate);
        point.x = point.x + 55;
        point.y = 100;
        chart.cursor.triggerMove(point, 'soft');
      } else {
        const point = dateAxis.dateToPoint(activeDate);
        point.x = point.x + 55;
        point.y = 100;
        chart.cursor.triggerMove(point, 'soft');
      }
    }
  }, [data, chart, hoveredDate, dateAxis]);
  return (
    <div className="graph_comm" style={{position: 'relative', width: 846}} onMouseLeave={()=>setHoveredDate(null)}>
      <div id="chartdiv" style={{width: '100%', height: 285}}/>
      <DateElement
        data={data} startDate={startDate} endDate={endDate} granularity={granularity}
        activeDate={activeDate} hoveredDate={hoveredDate} clickableRight={clickableRight} setHoveredDate={setHoveredDate}
        handleClickLeft={handleClickLeft} handleClickRight={handleClickRight} handleClickDate={handleClickDate}
      />
    </div>
  )
};

const DateElement = ({
                       data, startDate, endDate, granularity, activeDate, hoveredDate, clickableRight,
                       setHoveredDate, handleClickLeft, handleClickRight, handleClickDate
                     }) => {
  const [dates, setDates] = useState([]);
  const dayWidth = () => {
    if (granularity === 'day') {
      return 35.3;
    } else if (granularity === 'week') {
      return 52.9;
    } else if (granularity === 'month') {
      return 61.6;
    }
  };
  const getDateStyle = (date) => {
    if (activeDate && activeDate.getTime() === date.getTime()) {
      return {
        color: '#fff',
        backgroundColor: '#5F5F5F',
      };
    } else if (hoveredDate && hoveredDate.getTime() === date.getTime()) {
      return {
        color: '#fff',
        backgroundColor: '#5F5F5F',
      };
    } else {
      return {};
    }
  };
  const checkFirstDay = (index) => {
    if (dates.length === 0) {
      return false;
    }
    if (granularity !== 'month') {
      return index === 0 || dates[index].getMonth() !== dates[index - 1].getMonth();
    } else {
      return index === 0 || dates[index].getFullYear() !== dates[index - 1].getFullYear();
    }
  };
  useEffect(() => {
    const arr = [];
    if (granularity === 'day') {
      for (let i = moment(startDate); i <= moment(endDate); i = i.add(1, 'days')) {
        arr.push(i.toDate());
      }
    } else if (granularity === 'week') {
      for (let i = moment(startDate); i <= moment(endDate); i = i.add(1, 'weeks')) {
        arr.push(i.toDate());
      }
    } else if (granularity === 'month') {
      for (let i = moment(startDate); i <= moment(endDate); i = i.add(1, 'months')) {
        arr.push(i.toDate());
      }
    }
    setDates(arr);
  }, [startDate, endDate]);
  return (
    <div className="wrap_date  wrap_date_type1" style={{width: 'calc(100% - 29px)', marginLeft: 13}} onMouseLeave={()=>setHoveredDate(null)}>
      <div className="graph_date" style={{width: '100%'}} onMouseLeave={()=>setHoveredDate(null)}>
        <div className="inner_g" style={{display: 'flex', justifyContent: 'center'}}>
          {dates.map((date, index) => {
            return (
              <div className={classnames('data_day', checkFirstDay(index) ? 'fst_day' : '')} key={date}
                   style={{width: dayWidth()}}
                   onMouseEnter={()=>setHoveredDate(date)}
                   onClick={() => handleClickDate(date)}>
                {granularity !== 'month' &&
                <span className="num_month">{date.getMonth() + 1}월</span>
                }
                {granularity === 'month' &&
                <span className="num_month">{date.getFullYear()}년</span>
                }
                <span className="num_day" style={getDateStyle(date)}>
                  {granularity === 'month' ? date.getMonth() + 1 : date.getDate()}
                </span>
                <span className="ir_pm">{granularity === 'month' ? '월' : '일'}</span>
              </div>
            )
          })}
        </div>
      </div>
      <a className="link_graph_prev on" onClick={handleClickLeft}><span className="ico_blog ico_prev">이전</span></a>
      <a className={classnames('link_graph_next', clickableRight ? 'on' : '')} onClick={handleClickRight}>
        <span className="ico_blog ico_next">다음</span>
      </a>
    </div>
  )
};

export default LineColumnChart;